<template>
  <div class="layout-app">
    <dash-sidebar :user-name="userName" />
    <div class="layout-app__right">
      <dash-topbar :page-name="pageTitle" />
      <div class="layout-app__content">
        <router-view />
      </div>
    </div>
  </div>
</template>

<script>
import DashSidebar from '@/components/dashboard/DashSidebar.vue'
import DashTopbar  from '@/components/dashboard/DashTopbar.vue'

const PAGE_TITLES = {
  'app-dashboard':        'Dashboard',
  'DashboardChat':        'Dashboard',
  'webinar-simulator':    'Webinário',
  'integracoes':          'Integrações',
  'app-treinamentos':     'Treinamentos',
  'chats':                'Chats',
  'leads':                'Leads',
  'heatmap':              'Heatmap',
  'analytics':            'Analytics',
  'player':               'Player',
  'customizacao':         'Customização',
  'estilo':               'Estilo',
  'thumbnails':           'Thumbnails',
  'adicionar-videos':     'Novo Vídeo',
  'botoes-acoes':         'Botões e Ações',
  'teste-ab':             'Teste A/B',
  'fluxo':                'Fluxo',
  'fale-conosco':         'Suporte',
  'admin-overview':       'Overview',
  'admin-configuracoes':  'Configurações',
  'admin-usuarios':       'Usuários',
  'admin-blog':           'Blog',
  'admin-observabilidade':'Observabilidade',
  'admin-afiliados':      'Afiliados',
}

export default {
  name: 'LayoutApp',
  components: { DashSidebar, DashTopbar },
  computed: {
    userName() {
      try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}')
        return user.fullName || user.nome || user.name || 'User'
      } catch { return 'User' }
    },
    pageTitle() {
      return PAGE_TITLES[this.$route.name] || this.$route.meta?.pageTitle || 'App'
    },
  },
}
</script>

<style>
/* ── Shell ──────────────────────────────────────────────── */
.layout-app {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: #0D1117;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  position: relative;

  /* ── WarRoom CSS variables (used by Dashboard sub-components) ── */
  --wr-ink:         #07090D;
  --wr-ink-1:       #0B0F16;
  --wr-ink-2:       #10151F;
  --wr-ink-3:       #161D2B;
  --wr-line:        rgba(255,255,255,.06);
  --wr-line-2:      rgba(255,255,255,.10);
  --wr-line-strong: rgba(255,255,255,.16);
  --wr-text:        #FFFFFF;
  --wr-mute:        rgba(255,255,255,.58);
  --wr-dim:         rgba(255,255,255,.38);
  --wr-orange:      #E86A10;
  --wr-orange-2:    #F08023;
  --wr-gold:        #F5B935;
  --wr-green:       #28C76F;
  --wr-blue:        #318feb;
  --wr-danger:      #FF5A6B;
  --wr-purple:      #8B5CF6;
  --wr-pink:        #EC4899;
}

/* ── Right panel (topbar + scrollable content) ────────── */
.layout-app__right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.layout-app__content {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: #0D1117;
  padding: 28px 32px;

  /* CSS variables que todos os filhos que usam var(--dk-*) herdam */
  --dk-bg-page:       #0D1117;
  --dk-bg-card:       #111827;
  --dk-card-bg:       #111827;
  --dk-border:        rgba(255,255,255,.07);
  --dk-shadow-sm:     0 2px 8px rgba(0,0,0,.3);
  --dk-shadow-md:     0 6px 24px rgba(0,0,0,.4);
  --dk-text-primary:  #e2e8f0;
  --dk-text-secondary:#b8c2cc;
  --dk-text-muted:    rgba(255,255,255,.45);
  --dk-text-faint:    rgba(255,255,255,.2);
  --dk-bg-segment:    rgba(255,255,255,.06);
  --dk-bg-inset:      rgba(255,255,255,.04);
  --dk-bg-input:      #0f1724;
  --dk-border-input:  rgba(255,255,255,.1);
  --dk-border-light:  rgba(255,255,255,.05);
  --dk-bg-card-hover: #16202f;
}

/* ── Full-bleed pages: remove padding so they control their own layout ── */
.layout-app__content .dk-editar-page,
.layout-app__content .an-root,
.layout-app__content .hm-page {
  margin: -28px -32px !important;
  width: calc(100% + 64px) !important;
  max-width: none !important;
}

/* ── Container bootstrap override ───────────────────────── */
.layout-app__content .container {
  max-width: 100% !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* ── Legacy wrappers inside dashboard shell ─────────────────── */
.layout-app__content .app-content,
.layout-app__content .content-wrapper,
.layout-app__content .content-body,
.layout-app__content .content-area-wrapper {
  background: transparent !important;
  min-height: 100% !important;
}

/* ── Legacy light utility classes mapped to dashboard theme ─── */
.layout-app__content .bg-white {
  background: #111827 !important;
  color: #e2e8f0 !important;
}

.layout-app__content .bg-light {
  background: rgba(255,255,255,.06) !important;
  color: rgba(255,255,255,.75) !important;
}

.layout-app__content .text-dark {
  color: #e2e8f0 !important;
}

.layout-app__content .text-muted,
.layout-app__content .text-secondary {
  color: rgba(255,255,255,.45) !important;
}

/* ── Fallback for inline light backgrounds/text in old screens ─ */
.layout-app__content [style*="background: #fff"],
.layout-app__content [style*="background:#fff"],
.layout-app__content [style*="background-color: #fff"],
.layout-app__content [style*="background-color:#fff"],
.layout-app__content [style*="background: white"],
.layout-app__content [style*="background-color: white"] {
  background: #111827 !important;
  color: #e2e8f0 !important;
}

.layout-app__content [style*="color: #0f172a"],
.layout-app__content [style*="color:#0f172a"],
.layout-app__content [style*="color: #1e293b"],
.layout-app__content [style*="color:#1e293b"] {
  color: #e2e8f0 !important;
}

/* ── Tipografia geral ────────────────────────────────────── */
.layout-app__content,
.layout-app__content h1,
.layout-app__content h2,
.layout-app__content h3,
.layout-app__content h4,
.layout-app__content h5,
.layout-app__content p,
.layout-app__content span,
.layout-app__content label {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
}

/* ── Search bar ──────────────────────────────────────────── */
.layout-app__content .dk-search-field {
  background: #111827 !important;
  border-color: rgba(255,255,255,.1) !important;
}
.layout-app__content .dk-search-field .el-input__inner {
  background: transparent !important;
  color: #e2e8f0 !important;
}
.layout-app__content .dk-search-field .el-input__inner::placeholder {
  color: rgba(255,255,255,.25) !important;
}
.layout-app__content .dk-search-field__icon {
  color: rgba(255,255,255,.25) !important;
}

/* ── Stat chips ──────────────────────────────────────────── */
.layout-app__content .dk-stat-chip {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
  color: rgba(255,255,255,.45) !important;
}
.layout-app__content .dk-stat-chip__val {
  color: #e2e8f0 !important;
}

/* ── View toggle ─────────────────────────────────────────── */
.layout-app__content .dk-view-toggle {
  background: rgba(255,255,255,.06) !important;
}
.layout-app__content .dk-view-btn {
  color: rgba(255,255,255,.35) !important;
  background: transparent !important;
}
.layout-app__content .dk-view-btn--active {
  background: rgba(255,255,255,.1) !important;
  color: #E86A10 !important;
  box-shadow: none !important;
}

/* ── Folders header ──────────────────────────────────────── */
.layout-app__content .dk-folders-header {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
  border-left-color: rgba(232,106,16,.4) !important;
  box-shadow: none !important;
}
.layout-app__content .dk-folders-header__title {
  color: #e2e8f0 !important;
}

/* ── Folder cards ────────────────────────────────────────── */
.layout-app__content .dk-folder-card {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
  box-shadow: none !important;
}
.layout-app__content .dk-folder-card:hover {
  background: #16202f !important;
  border-color: rgba(232,106,16,.35) !important;
  box-shadow: 0 6px 24px rgba(0,0,0,.35) !important;
}
.layout-app__content .dk-folder-card__name {
  color: #e2e8f0 !important;
  font-size: 0.88rem !important;
}
.layout-app__content .dk-folder-card__date {
  color: rgba(255,255,255,.35) !important;
  font-size: 0.75rem !important;
}
.layout-app__content .dk-folder-card__more-btn,
.layout-app__content .dk-folder-card__edit-btn {
  color: rgba(255,255,255,.4) !important;
}

/* ── Breadcrumb ──────────────────────────────────────────── */
.layout-app__content .dk-breadcrumb__link {
  color: rgba(255,255,255,.4) !important;
}
.layout-app__content .dk-breadcrumb__link--active {
  color: #e2e8f0 !important;
  background: rgba(232,106,16,.08) !important;
  border-color: rgba(232,106,16,.15) !important;
}
.layout-app__content .dk-breadcrumb__link:hover {
  color: #E86A10 !important;
}

/* ── Video cards (list mode) ─────────────────────────────── */
.layout-app__content .dk-vc {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
  border-left-color: rgba(232,106,16,.2) !important;
  box-shadow: none !important;
}
.layout-app__content .dk-vc:hover {
  background: #16202f !important;
  border-color: rgba(232,106,16,.3) !important;
  border-left-color: #E86A10 !important;
  box-shadow: 0 4px 20px rgba(0,0,0,.3) !important;
  transform: translateY(-1px);
}
.layout-app__content .dk-vc__title {
  color: #e2e8f0 !important;
  font-weight: 600 !important;
}
.layout-app__content .dk-vc__title-input {
  background: rgba(255,255,255,.06) !important;
  color: #e2e8f0 !important;
  border: 1px solid rgba(255,255,255,.12) !important;
  border-radius: 6px !important;
}
.layout-app__content .dk-vc__meta {
  color: rgba(255,255,255,.38) !important;
}
.layout-app__content .dk-vc__orient,
.layout-app__content .dk-vc__date {
  color: rgba(255,255,255,.38) !important;
}
.layout-app__content .dk-vc__duration {
  background: rgba(0,0,0,.65) !important;
  color: #fff !important;
}
.layout-app__content .dk-vc__views {
  color: rgba(255,255,255,.35) !important;
  font-size: 11px !important;
}
.layout-app__content .dk-vc__check-box {
  background: transparent !important;
  border-color: rgba(255,255,255,.2) !important;
}

/* ── Action buttons ──────────────────────────────────────── */
.layout-app__content .dk-vc__action-btn {
  background: rgba(255,255,255,.06) !important;
  color: rgba(255,255,255,.45) !important;
  border: 1px solid rgba(255,255,255,.06) !important;
  border-radius: 8px !important;
  width: 32px !important;
  height: 32px !important;
}
.layout-app__content .dk-vc__action-btn:hover {
  background: rgba(232,106,16,.15) !important;
  color: #E86A10 !important;
  border-color: rgba(232,106,16,.25) !important;
}
.layout-app__content .dk-vc__edit-btn,
.layout-app__content .dk-vc__save-btn {
  color: rgba(255,255,255,.4) !important;
}

/* ── Skeleton ────────────────────────────────────────────── */
.layout-app__content .dk-sk {
  background: rgba(255,255,255,.07) !important;
}

/* ── Empty state ─────────────────────────────────────────── */
.layout-app__content .dk-vl-empty {
  padding: 60px 0 !important;
}
.layout-app__content .dk-vl-empty__icon {
  color: rgba(255,255,255,.12) !important;
}
.layout-app__content .dk-vl-empty__text {
  color: rgba(255,255,255,.55) !important;
  font-weight: 600 !important;
}
.layout-app__content .dk-vl-empty__sub {
  color: rgba(255,255,255,.3) !important;
}
.layout-app__content .dk-folders-empty__icon {
  color: rgba(255,255,255,.12) !important;
}
.layout-app__content .dk-folders-empty__text {
  color: rgba(255,255,255,.55) !important;
}
.layout-app__content .dk-folders-empty__sub {
  color: rgba(255,255,255,.3) !important;
}

/* ── Dropdown menus (Element UI) ─────────────────────────── */
.el-dropdown-menu {
  background: #1a2235 !important;
  border-color: rgba(255,255,255,.08) !important;
  box-shadow: 0 8px 32px rgba(0,0,0,.5) !important;
}
.el-dropdown-menu__item {
  color: rgba(255,255,255,.7) !important;
}
.el-dropdown-menu__item:hover,
.el-dropdown-menu__item:focus {
  background: rgba(255,255,255,.07) !important;
  color: #fff !important;
}
.el-dropdown-menu__item--divided {
  border-top-color: rgba(255,255,255,.07) !important;
}
.el-dropdown-menu .dk-dd-item--danger {
  color: #ff6b6b !important;
}
.el-popper .popper__arrow::after {
  border-bottom-color: #1a2235 !important;
}

/* ══════════════════════════════════════════════════════════
   LEADS PAGE — dark overrides
   ══════════════════════════════════════════════════════════ */

/* ── Topbar buttons ──────────────────────────────────────── */
.layout-app__content .dk-view-btn {
  background: rgba(255,255,255,.06) !important;
  color: rgba(255,255,255,.5) !important;
  border-color: rgba(255,255,255,.08) !important;
}
.layout-app__content .dk-view-btn--active {
  background: rgba(232,106,16,.12) !important;
  color: #E86A10 !important;
  border-color: rgba(232,106,16,.3) !important;
}
.layout-app__content .dk-cols-btn {
  background: rgba(255,255,255,.06) !important;
  color: rgba(255,255,255,.5) !important;
  border-color: rgba(255,255,255,.08) !important;
}
.layout-app__content .dk-cols-btn:hover,
.layout-app__content .dk-cols-btn--selecting {
  background: rgba(232,106,16,.12) !important;
  color: #E86A10 !important;
  border-color: rgba(232,106,16,.3) !important;
}
.layout-app__content .dk-pipeline-filter {
  background: rgba(255,255,255,.06) !important;
  border-color: rgba(255,255,255,.08) !important;
}
.layout-app__content .dk-pipeline-filter__select {
  background: transparent !important;
  color: rgba(255,255,255,.6) !important;
  border-color: transparent !important;
}

/* ── Column config panel ─────────────────────────────────── */
.layout-app__content .dk-col-config {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-col-config__name {
  background: transparent !important;
  color: rgba(255,255,255,.7) !important;
  border-color: rgba(255,255,255,.08) !important;
}

/* ── Kanban board ────────────────────────────────────────── */
.layout-app__content .kb-column {
  background: rgba(255,255,255,.03) !important;
  border: 1px solid rgba(255,255,255,.06) !important;
}
.layout-app__content .kb-column__header {
  background: #111827 !important;
  border-bottom: 1px solid rgba(255,255,255,.06) !important;
}
.layout-app__content .kb-column__title {
  color: #e2e8f0 !important;
}
.layout-app__content .kb-column__count {
  background: rgba(255,255,255,.07) !important;
  color: rgba(255,255,255,.4) !important;
}

/* ── Kanban card ─────────────────────────────────────────── */
.layout-app__content .kb-card {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
  box-shadow: none !important;
}
.layout-app__content .kb-card:hover {
  background: #16202f !important;
  border-color: rgba(255,255,255,.12) !important;
  box-shadow: 0 4px 16px rgba(0,0,0,.3) !important;
}
.layout-app__content .kb-card--selected {
  background: rgba(124,58,237,.08) !important;
  border-color: rgba(124,58,237,.35) !important;
}
.layout-app__content .kb-card__name {
  color: #e2e8f0 !important;
}
.layout-app__content .kb-card__flow {
  color: rgba(255,255,255,.35) !important;
}
.layout-app__content .kb-card__tag--email {
  background: rgba(37,99,235,.12) !important;
  color: #7eb3ff !important;
}
.layout-app__content .kb-card__tag--phone {
  background: rgba(5,150,105,.12) !important;
  color: #4fd18b !important;
}
.layout-app__content .kb-card__source {
  background: rgba(232,106,16,.12) !important;
  color: #E86A10 !important;
}
.layout-app__content .kb-card__date {
  color: rgba(255,255,255,.3) !important;
}
.layout-app__content .kb-card__select-check {
  background: rgba(255,255,255,.06) !important;
  border-color: rgba(255,255,255,.2) !important;
}

/* ── Leads header ────────────────────────────────────────── */
.layout-app__content .dk-leads-header {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}
.layout-app__content .dk-leads-header__title {
  color: #e2e8f0 !important;
}
.layout-app__content .dk-leads-header__sub {
  color: rgba(255,255,255,.4) !important;
}
.layout-app__content .dk-leads-header__icon-wrap {
  background: rgba(232,106,16,.12) !important;
  color: #E86A10 !important;
}

/* ── Leads toolbar (search/filter) ──────────────────────── */
.layout-app__content .dk-leads-toolbar {
  background: transparent !important;
  border: none !important;
}
.layout-app__content .dk-leads-search {
  background: #111827 !important;
  border-color: rgba(255,255,255,.1) !important;
}
.layout-app__content .dk-toolbar-select-wrap {
  background: #111827 !important;
  border-color: rgba(255,255,255,.1) !important;
}
.layout-app__content .dk-per-page__select {
  background: #111827 !important;
  border-color: rgba(255,255,255,.1) !important;
  color: rgba(255,255,255,.6) !important;
}

/* ── Leads table ─────────────────────────────────────────── */
.layout-app__content .dk-leads-table-wrap {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-leads-table thead th {
  background: rgba(255,255,255,.03) !important;
  color: rgba(255,255,255,.5) !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-leads-table td {
  border-color: rgba(255,255,255,.05) !important;
  color: rgba(255,255,255,.7) !important;
}
.layout-app__content .dk-leads-table tr:hover td {
  background: rgba(255,255,255,.02) !important;
}
.layout-app__content .dk-lead-name-text {
  color: #e2e8f0 !important;
}
.layout-app__content .dk-lead-avatar {
  background: rgba(232,106,16,.35) !important;
}

/* ── Pagination ──────────────────────────────────────────── */
.layout-app__content .dk-leads-pagination {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-leads-pagination__info {
  color: rgba(255,255,255,.35) !important;
}

/* ── Trial overlay ───────────────────────────────────────── */
.layout-app__content .dk-trial-leads-overlay {
  background: linear-gradient(to bottom, transparent 0%, rgba(13,17,23,.95) 40%, #0D1117 100%) !important;
}
.layout-app__content .dk-trial-leads-overlay__card {
  background: #111827 !important;
  border-color: rgba(232,106,16,.2) !important;
}
.layout-app__content .dk-trial-leads-overlay__title {
  color: #e2e8f0 !important;
}
.layout-app__content .dk-trial-leads-overlay__text {
  color: rgba(255,255,255,.45) !important;
}

/* ═══════════════════════════════════════════════════════════
   ADMIN PAGES — dark overrides
   ══════════════════════════════════════════════════════════ */

/* ── Admin Configurações wrapper ────────────────────────── */
.layout-app__content .adm-config-title {
  color: #e2e8f0 !important;
}
.layout-app__content .adm-config-sub {
  color: rgba(255,255,255,.45) !important;
}
.layout-app__content .adm-config-tabs {
  border-bottom-color: rgba(255,255,255,.08) !important;
}
.layout-app__content .adm-config-tab {
  color: rgba(255,255,255,.4) !important;
}
.layout-app__content .adm-config-tab:hover {
  color: #E86A10 !important;
}
.layout-app__content .adm-config-tab--active {
  color: #E86A10 !important;
  border-bottom-color: #E86A10 !important;
}

/* ══════════════════════════════════════════════════════════
   GLOBAL DARK — headings, text, cards, tables, forms
   ══════════════════════════════════════════════════════════ */

/* Headings */
.layout-app__content h1:not([class]),
.layout-app__content h2:not([class]),
.layout-app__content h3:not([class]),
.layout-app__content h4:not([class]),
.layout-app__content h5:not([class]),
.layout-app__content h6:not([class]) {
  color: #e2e8f0 !important;
}

/* Generic muted text */
.layout-app__content p:not([class]) {
  color: rgba(255,255,255,.6) !important;
}
.layout-app__content small:not([class]) {
  color: rgba(255,255,255,.4) !important;
}

/* Bootstrap Vue / Bootstrap cards */
.layout-app__content .card {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
  box-shadow: 0 2px 12px rgba(0,0,0,.3) !important;
}
.layout-app__content .card-header {
  background: #111827 !important;
  border-bottom-color: rgba(255,255,255,.07) !important;
  color: #e2e8f0 !important;
}
.layout-app__content .card-body { color: rgba(255,255,255,.75) !important; }
.layout-app__content .card-title { color: #e2e8f0 !important; }
.layout-app__content .card-text { color: rgba(255,255,255,.6) !important; }
.layout-app__content .card-footer {
  background: rgba(255,255,255,.03) !important;
  border-top-color: rgba(255,255,255,.07) !important;
}

/* Bootstrap nav-tabs */
.layout-app__content .nav-tabs {
  border-bottom-color: rgba(255,255,255,.08) !important;
}
.layout-app__content .nav-tabs .nav-link {
  color: rgba(255,255,255,.4) !important;
  background: transparent !important;
  border-color: transparent !important;
}
.layout-app__content .nav-tabs .nav-link.active,
.layout-app__content .nav-tabs .nav-item.show .nav-link {
  background: transparent !important;
  border-color: transparent transparent #E86A10 !important;
  color: #E86A10 !important;
}
.layout-app__content .tab-content { background: transparent !important; }

/* Bootstrap tables */
.layout-app__content .table {
  color: #e2e8f0 !important;
}
.layout-app__content .table td,
.layout-app__content .table th {
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .table thead th {
  background: rgba(255,255,255,.04) !important;
  color: rgba(255,255,255,.55) !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
  letter-spacing: .04em !important;
  text-transform: uppercase !important;
  border-bottom-color: rgba(255,255,255,.08) !important;
}
.layout-app__content .table tbody tr:hover td {
  background: rgba(255,255,255,.03) !important;
}
.layout-app__content .table-striped tbody tr:nth-of-type(odd) {
  background: rgba(255,255,255,.02) !important;
}
.layout-app__content .b-table-empty-row td {
  color: rgba(255,255,255,.35) !important;
}

/* Bootstrap forms */
.layout-app__content .form-control,
.layout-app__content .custom-select {
  background: #0f1724 !important;
  border-color: rgba(255,255,255,.1) !important;
  color: #e2e8f0 !important;
}
.layout-app__content .form-control::placeholder { color: rgba(255,255,255,.25) !important; }
.layout-app__content .form-control:focus {
  border-color: #E86A10 !important;
  box-shadow: 0 0 0 3px rgba(232,106,16,.15) !important;
  background: #0f1724 !important;
}
.layout-app__content .input-group-text {
  background: #0f1724 !important;
  border-color: rgba(255,255,255,.1) !important;
  color: rgba(255,255,255,.45) !important;
}
.layout-app__content label {
  color: rgba(255,255,255,.7) !important;
}
.layout-app__content .form-text { color: rgba(255,255,255,.35) !important; }

/* Buttons */
.layout-app__content .btn-primary {
  background: linear-gradient(135deg, #E86A10, #F5B935) !important;
  border-color: #E86A10 !important;
  color: #fff !important;
}
.layout-app__content .btn-secondary {
  background: rgba(255,255,255,.08) !important;
  border-color: rgba(255,255,255,.1) !important;
  color: rgba(255,255,255,.7) !important;
}
.layout-app__content .btn-outline-primary {
  border-color: #E86A10 !important;
  color: #E86A10 !important;
}
.layout-app__content .btn-outline-primary:hover {
  background: rgba(232,106,16,.15) !important;
}
.layout-app__content .btn-danger {
  background: rgba(248,113,113,.15) !important;
  border-color: rgba(248,113,113,.3) !important;
  color: #f87171 !important;
}

/* Bootstrap badge */
.layout-app__content .badge-light {
  background: rgba(255,255,255,.08) !important;
  color: rgba(255,255,255,.6) !important;
}

/* Page title / breadcrumb Vuexy */
.layout-app__content .content-header-title {
  color: #e2e8f0 !important;
}
.layout-app__content .breadcrumb {
  background: transparent !important;
}
.layout-app__content .breadcrumb-item,
.layout-app__content .breadcrumb-item + .breadcrumb-item::before {
  color: rgba(255,255,255,.4) !important;
}
.layout-app__content .breadcrumb-item a {
  color: rgba(255,255,255,.4) !important;
}
.layout-app__content .breadcrumb-item.active {
  color: rgba(255,255,255,.7) !important;
}

/* Bootstrap modal inside LayoutApp */
.layout-app__content .modal-content {
  background: #111827 !important;
  border-color: rgba(255,255,255,.08) !important;
}
.layout-app__content .modal-header {
  border-bottom-color: rgba(255,255,255,.07) !important;
  color: #e2e8f0 !important;
}
.layout-app__content .modal-footer {
  border-top-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .modal-title { color: #e2e8f0 !important; }

/* Element UI dialog */
.layout-app__content .el-dialog {
  background: #111827 !important;
  border: 1px solid rgba(255,255,255,.07) !important;
}
.layout-app__content .el-dialog__header {
  border-bottom: 1px solid rgba(255,255,255,.07) !important;
}
.layout-app__content .el-dialog__title { color: #e2e8f0 !important; }
.layout-app__content .el-dialog__body { color: rgba(255,255,255,.7) !important; }

/* General text colour cleanup */
.layout-app__content [style*="color: #0f172a"],
.layout-app__content [style*="color:#0f172a"],
.layout-app__content [style*="color: #1e293b"] {
  color: #e2e8f0 !important;
}

/* ══════════════════════════════════════════════════════════
   PLAYER / EDITAR VÍDEO — dark overrides
   ══════════════════════════════════════════════════════════ */

/* ── Sidebar ─────────────────────────────────────────────── */
.layout-app__content .dk-editar-sidebar {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-sidebar-back {
  color: rgba(255,255,255,.45) !important;
}
.layout-app__content .dk-sidebar-back:hover {
  color: #e2e8f0 !important;
}
.layout-app__content .dk-sidebar-divider {
  background: rgba(255,255,255,.06) !important;
}
.layout-app__content .dk-sidebar-video-info {
  background: rgba(255,255,255,.03) !important;
}
.layout-app__content .dk-sidebar-thumb {
  background: rgba(255,255,255,.06) !important;
  color: rgba(255,255,255,.35) !important;
}
.layout-app__content .dk-sidebar-video-info__name {
  color: #e2e8f0 !important;
}
.layout-app__content .dk-sidebar-item {
  color: rgba(255,255,255,.55) !important;
}
.layout-app__content .dk-sidebar-item:hover {
  background: rgba(255,255,255,.04) !important;
  border-left-color: rgba(232,106,16,.3) !important;
}
.layout-app__content .dk-sidebar-item--active {
  background: rgba(232,106,16,.08) !important;
  border-left-color: #E86A10 !important;
}
.layout-app__content .dk-sidebar-item__icon-wrap {
  background: rgba(255,255,255,.05) !important;
  color: rgba(255,255,255,.35) !important;
}
.layout-app__content .dk-sidebar-item--active .dk-sidebar-item__icon-wrap,
.layout-app__content .dk-sidebar-item:hover .dk-sidebar-item__icon-wrap {
  background: rgba(232,106,16,.12) !important;
  color: #E86A10 !important;
}
.layout-app__content .dk-sidebar-item__label {
  color: rgba(255,255,255,.7) !important;
}
.layout-app__content .dk-sidebar-item__sub {
  color: rgba(255,255,255,.35) !important;
}
.layout-app__content .dk-sidebar-item--active .dk-sidebar-item__label {
  color: #E86A10 !important;
}

/* ── Header ─────────────────────────────────────────────── */
.layout-app__content .dk-editar-header {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-editar-header__title {
  color: #e2e8f0 !important;
}
.layout-app__content .dk-editar-header__edit-btn {
  color: rgba(255,255,255,.35) !important;
  border-color: rgba(255,255,255,.1) !important;
}
.layout-app__content .dk-editar-header__edit-btn:hover {
  color: #E86A10 !important;
  border-color: #E86A10 !important;
}
.layout-app__content .dk-btn-test {
  color: #E86A10 !important;
  border-color: rgba(232,106,16,.5) !important;
  background: transparent !important;
}
.layout-app__content .dk-btn-test:hover {
  background: rgba(232,106,16,.1) !important;
}
.layout-app__content .dk-btn-embed {
  background: rgba(255,255,255,.08) !important;
  color: rgba(255,255,255,.8) !important;
  border: 1px solid rgba(255,255,255,.1) !important;
}
.layout-app__content .dk-btn-embed:hover {
  background: rgba(255,255,255,.12) !important;
}

/* ── Player badge / shell ────────────────────────────────── */
.layout-app__content .dk-player-badge {
  background: rgba(255,255,255,.05) !important;
  border-color: rgba(255,255,255,.08) !important;
  color: rgba(255,255,255,.4) !important;
}
.layout-app__content .dk-player-shell {
  background: #0D1117 !important;
}

/* ── Edit form ───────────────────────────────────────────── */
.layout-app__content .dk-editar-form {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-form-label {
  color: rgba(255,255,255,.45) !important;
}
.layout-app__content .dk-form-input {
  background: #0f1724 !important;
  border-color: rgba(255,255,255,.1) !important;
  color: #e2e8f0 !important;
}
.layout-app__content .dk-form-input:focus {
  border-color: #E86A10 !important;
}
.layout-app__content .dk-btn-cancel {
  background: rgba(255,255,255,.06) !important;
  border-color: rgba(255,255,255,.1) !important;
  color: rgba(255,255,255,.55) !important;
}

/* ── Embed modal ─────────────────────────────────────────── */
.layout-app__content .dk-embed-modal {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-embed-modal__title {
  color: #e2e8f0 !important;
}
.layout-app__content .dk-embed-modal__sub {
  color: rgba(255,255,255,.4) !important;
}
.layout-app__content .dk-embed-modal__tabs {
  background: rgba(255,255,255,.04) !important;
}
.layout-app__content .dk-embed-tab {
  color: rgba(255,255,255,.4) !important;
}
.layout-app__content .dk-embed-tab:hover {
  background: rgba(255,255,255,.06) !important;
  color: #e2e8f0 !important;
}
.layout-app__content .dk-embed-tab--active {
  background: #E86A10 !important;
  color: #fff !important;
}
.layout-app__content .dk-embed-modal__close-btn {
  background: rgba(255,255,255,.06) !important;
  color: rgba(255,255,255,.55) !important;
}
.layout-app__content .dk-embed-modal__close-btn:hover {
  background: rgba(255,255,255,.12) !important;
  color: #e2e8f0 !important;
}

/* ══════════════════════════════════════════════════════════
   ANALYTICS PAGE — dark overrides
   ══════════════════════════════════════════════════════════ */

.layout-app__content .an-root {
  background: #0D1117 !important;
  min-height: 0 !important;
}
.layout-app__content .an-sidebar {
  background: #111827 !important;
  box-shadow: 2px 0 12px rgba(0,0,0,.35) !important;
}
.layout-app__content .an-back-btn {
  color: rgba(255,255,255,.55) !important;
}
.layout-app__content .an-sidebar-divider {
  background: rgba(255,255,255,.06) !important;
}
.layout-app__content .an-sidebar-header {
  border-bottom-color: rgba(255,255,255,.06) !important;
}
.layout-app__content .an-header-title {
  color: #e2e8f0 !important;
}
.layout-app__content .an-nav-item {
  color: rgba(255,255,255,.45) !important;
}
.layout-app__content .an-nav-item:hover {
  background: rgba(255,255,255,.05) !important;
  color: #e2e8f0 !important;
}
.layout-app__content .an-body {
  background: #0D1117 !important;
  padding: 0 !important;
}

/* ── Detalhes (stats) component ──────────────────────────── */
.layout-app__content .dt-wrap {
  background: #0D1117 !important;
  color: #e2e8f0 !important;
}
.layout-app__content .dt-topbar {
  background: #111827 !important;
  border-bottom-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dt-video-name {
  color: #e2e8f0 !important;
}
.layout-app__content .dt-kpis {
  background: #111827 !important;
  border-bottom-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dt-kpi {
  border-right-color: rgba(255,255,255,.06) !important;
}
.layout-app__content .dt-player-card {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dt-card-header {
  background: rgba(255,255,255,.02) !important;
  border-bottom-color: rgba(255,255,255,.06) !important;
}
.layout-app__content .dt-daterange {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
  margin: 0 !important;
  border-radius: 0 !important;
  border-left: none !important;
  border-right: none !important;
}

/* ═══════════════════════════════════════════════════════════
   INTEGRAÇÕES PAGE — dark overrides
   ══════════════════════════════════════════════════════════ */

/* ── Card wrapper ────────────────────────────────────────── */
.layout-app__content .dk-int-card {
  border-color: rgba(255,255,255,.07) !important;
  box-shadow: 0 2px 12px rgba(0,0,0,.3) !important;
}

/* ── Empty state ─────────────────────────────────────────── */
.layout-app__content .dk-empty-text {
  color: #e2e8f0 !important;
}

/* ── Platform cards ──────────────────────────────────────── */
.layout-app__content .dk-platform-card {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-platform-card:hover {
  border-color: #E86A10 !important;
  box-shadow: 0 2px 12px rgba(232,106,16,.12) !important;
}
.layout-app__content .dk-platform-logo-wrap {
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-platform-name {
  color: #e2e8f0 !important;
}
.layout-app__content .dk-platform-btn {
  background: rgba(255,255,255,.08) !important;
  color: rgba(255,255,255,.8) !important;
  border: 1px solid rgba(255,255,255,.1) !important;
}
.layout-app__content .dk-platform-btn:hover {
  background: #E86A10 !important;
  color: #fff !important;
  border-color: #E86A10 !important;
}

/* ── Integrations table ──────────────────────────────────── */
.layout-app__content .dk-table thead tr {
  background: rgba(255,255,255,.03) !important;
  border-bottom-color: rgba(255,255,255,.08) !important;
}
.layout-app__content .dk-table td {
  border-bottom-color: rgba(255,255,255,.05) !important;
}
.layout-app__content .dk-table .dk-table-row:hover td {
  background: rgba(255,255,255,.02) !important;
}
.layout-app__content .dk-flow-name {
  color: #e2e8f0 !important;
}

/* ── Model badges ────────────────────────────────────────── */
.layout-app__content .dk-model-badge {
  background: rgba(255,255,255,.07) !important;
  color: rgba(255,255,255,.75) !important;
}
.layout-app__content .dk-model-badge.dk-model-activecampaign {
  background: rgba(192,89,0,.15) !important;
  color: #f59e0b !important;
}
.layout-app__content .dk-model-badge.dk-model-pipedrive {
  background: rgba(26,122,69,.15) !important;
  color: #4fd18b !important;
}
.layout-app__content .dk-model-badge.dk-model-webhook {
  background: rgba(107,63,160,.15) !important;
  color: #a78bfa !important;
}
.layout-app__content .dk-model-badge.dk-model-chatgpt {
  background: rgba(26,122,107,.15) !important;
  color: #34d399 !important;
}
.layout-app__content .dk-model-badge.dk-model-rdstation {
  background: rgba(181,32,32,.15) !important;
  color: #f87171 !important;
}
.layout-app__content .dk-model-badge.dk-model-nocrm {
  background: rgba(26,74,138,.15) !important;
  color: #7eb3ff !important;
}

/* ── Action buttons ──────────────────────────────────────── */
.layout-app__content .dk-action-btn.dk-action-delete {
  background: rgba(193,43,43,.1) !important;
  border-color: rgba(193,43,43,.25) !important;
  color: #f87171 !important;
}
.layout-app__content .dk-action-btn.dk-action-edit {
  background: rgba(255,255,255,.06) !important;
  border-color: rgba(255,255,255,.1) !important;
  color: rgba(255,255,255,.6) !important;
}
.layout-app__content .dk-action-btn.dk-action-edit:hover {
  background: rgba(255,255,255,.12) !important;
  color: #e2e8f0 !important;
}
.layout-app__content .dk-action-btn.dk-action-test {
  background: rgba(245,185,53,.08) !important;
  border-color: rgba(245,185,53,.2) !important;
  color: #F5B935 !important;
}

/* ── Modal fields ────────────────────────────────────────── */
.layout-app__content .dk-modal-body {
  background: #111827 !important;
}
.layout-app__content .dk-mlabel {
  color: rgba(255,255,255,.45) !important;
}
.layout-app__content .dk-minput {
  background: #0f1724 !important;
  border-color: rgba(255,255,255,.1) !important;
  color: #e2e8f0 !important;
}
.layout-app__content .dk-minput:focus {
  background: #0f1724 !important;
  border-color: #E86A10 !important;
}
.layout-app__content .dk-mselect {
  background: #0f1724 !important;
  border-color: rgba(255,255,255,.1) !important;
  color: #e2e8f0 !important;
}
.layout-app__content .dk-mbtn-cancel {
  background: rgba(255,255,255,.06) !important;
  color: rgba(255,255,255,.55) !important;
}
.layout-app__content .dk-mbtn-cancel:hover {
  background: rgba(255,255,255,.1) !important;
}

/* ── Event checkboxes ────────────────────────────────────── */
.layout-app__content .dk-event-check-item {
  background: rgba(255,255,255,.03) !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .dk-event-check-item:hover {
  border-color: rgba(232,106,16,.3) !important;
}
.layout-app__content .dk-event-check-name {
  color: #e2e8f0 !important;
}

/* ══════════════════════════════════════════════════════════
   WEBINAR SIMULATOR — dark overrides for sub-components
   ══════════════════════════════════════════════════════════ */

/* ── StreamConfig / ChatSimulator / ChatTriggers cards ────── */
.layout-app__content .sc-card,
.layout-app__content .cs-root,
.layout-app__content .ct-root {
  background: #111827 !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .sc-card-header,
.layout-app__content .sc-section-title {
  color: rgba(255,255,255,.75) !important;
  border-bottom-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .sc-video-uploaded {
  background: rgba(255,255,255,.04) !important;
  border-color: rgba(255,255,255,.07) !important;
}

/* ── Auth card (YouTubeAuth uses .b-card — covered by global) ── */
.layout-app__content .auth-header {
  background: #16202f !important;
  border-bottom-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .status-item {
  background: rgba(255,255,255,.03) !important;
  border-color: rgba(255,255,255,.07) !important;
}
.layout-app__content .status-label {
  color: rgba(255,255,255,.75) !important;
}
.layout-app__content .divider {
  border-color: rgba(255,255,255,.07) !important;
}
</style>
